// --------------------------------------------------------------
// Created On: 2021-06-01
// Author: Zachary Thomas
//
// Last Modified: 2026-05-07
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo, useState } from "react";
import IconTooltip from "../IconTooltip/IconTooltip";
import translateManufacturer from "../../utilities/translateManufacturer";
import {
  API,
  SAMPLING_SUPPORTED_MONITORING_DEVICES,
  VALIDATION_SUPPORTED_MONITORING_DEVICES,
} from "../../constants/miscellaneous";
import useApi from "../../hooks/useApi";
import useReduxSelector from "../../hooks/useReduxSelector";
import SampleDeviceDataButton from "../SampleDeviceDataButton/SampleDeviceDataButton";
import ValidateAssetButton from "../ValidateAssetButton/ValidateAssetButton";
import { Link } from "react-router-dom";
import styles from "./AssetDetails.module.scss";

// Panel that displays information about the current asset.
export default function AssetDetails(props: Props): Component {
  const [assetgroupsLabel, setAssetgroupsLabel] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);
  const gatewayAsset = useMemo(() => getGatewayAsset(props.networkAssets), [JSON.stringify(props.networkAssets)]);
  const deviceCanSample = useMemo(
    () => getDeviceCanSample(props.deviceType, props.assetId, gatewayAsset),
    [props.deviceType, props.assetId, JSON.stringify(gatewayAsset)]
  );
  const deviceCanValidate = useMemo(
    () => getDeviceCanValidate(props.deviceType, props.assetId, gatewayAsset),
    [props.deviceType, props.assetId, JSON.stringify(gatewayAsset)]
  );

  // Get the associated assetgroups for this asset if 'showAssociatedAssetgroups' is true.
  useApi(
    () => {
      if (props.assetId !== undefined && props.showAssociatedAssetgroups !== undefined) {
        return props.showAssociatedAssetgroups;
      } else {
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${props.assetId}/assetgroup`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setAssetgroupsLabel(getAssetgroupLabel(responseBody.assetgroups));
      } else {
        setAssetgroupsLabel("");
      }
    },
    [props.assetId, props.showAssociatedAssetgroups]
  );

  // Returns network asset information about the gateway asset.
  function getGatewayAsset(networkAssets: NetworkAsset[] | undefined): NetworkAsset | null {
    let gatewayAsset: NetworkAsset | null | undefined;
    if (networkAssets !== undefined) {
      gatewayAsset = networkAssets.find((networkAsset) => networkAsset.isGateway);
    }
    if (gatewayAsset === undefined) {
      gatewayAsset = null;
    }
    return gatewayAsset;
  }

  // Returns if the current device supports sampling.
  function getDeviceCanSample(
    deviceType: string | null,
    assetId: number | undefined,
    gatewayAsset: NetworkAsset | null
  ): boolean {
    const assetMonitoringDeviceCanSample =
      SAMPLING_SUPPORTED_MONITORING_DEVICES.includes(deviceType || "") && assetId !== null && assetId !== undefined;
    const networkMonitoringDeviceCanSample =
      gatewayAsset !== null &&
      SAMPLING_SUPPORTED_MONITORING_DEVICES.includes(gatewayAsset.networkDeviceTypeModel || "");
    return assetMonitoringDeviceCanSample || networkMonitoringDeviceCanSample;
  }

  // Returns if the current device supports validation.
  function getDeviceCanValidate(
    deviceType: string | null,
    assetId: number | undefined,
    gatewayAsset: NetworkAsset | null
  ): boolean {
    const assetMonitoringDeviceCanValidate =
      VALIDATION_SUPPORTED_MONITORING_DEVICES.includes(deviceType || "") && assetId !== null && assetId !== undefined;
    const networkMonitoringDeviceCanValidate =
      gatewayAsset !== null &&
      VALIDATION_SUPPORTED_MONITORING_DEVICES.includes(gatewayAsset.networkDeviceTypeModel || "");
    return assetMonitoringDeviceCanValidate || networkMonitoringDeviceCanValidate;
  }

  // Returns the list of associated asset groups as a comma separated string.
  function getAssetgroupLabel(assetgroups: AssociatedAssetgroup[]): string {
    const names: string[] = [];
    assetgroups.forEach((assetgroup) => names.push(assetgroup.name));
    return names.join(", ");
  }

  // Checks if the current asset has other assets in the same network.
  function otherAssetsExistInNetwork(
    isInNetwork: boolean | undefined,
    networkAssets: NetworkAsset[] | null | undefined
  ): boolean {
    return isInNetwork === true && networkAssets !== null && networkAssets !== undefined && networkAssets.length > 0;
  }

  // Checks if the current asset is part of a network with another asset that has a monitoring device.
  function otherAssetInNetworkHasMonitoringDevice(
    isInNetwork: boolean | undefined,
    gatewayAsset: NetworkAsset | null
  ): boolean {
    return (
      isInNetwork === true &&
      gatewayAsset !== null &&
      gatewayAsset.networkDeviceTypeName !== null &&
      gatewayAsset.networkDeviceIdentifier !== null
    );
  }

  return (
    <div className="px-3 pt-4 pb-3" data-test="asset-details-container">
      <div className="row justify-content-center">
        <div className="table-responsive col-12 col-xl-8">
          <table className={`${styles.table} table mb-0 pb-0`}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>Asset Details</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Name</td>
                <td data-test="asset-details-name">
                  <span className="me-2">{props.name}</span>
                  {props.isRented && (
                    <span>
                      {" "}
                      <IconTooltip
                        id="rental"
                        icon="file-text"
                        message="This asset is a rental. Your ability to modify this asset is limited."
                        color="var(--alert-info)"
                      />
                    </span>
                  )}
                </td>
              </tr>

              <tr>
                <td>Nickname</td>
                {(props.nickname !== null && props.nickname !== undefined && props.nickname.length > 0) ||
                props.loading ? (
                  <td data-test="asset-details-nickname">{props.nickname}</td>
                ) : (
                  <td data-test="asset-details-nickname">None</td>
                )}
              </tr>

              {props.productType !== null && props.productType !== undefined && (
                <tr>
                  <td>Product Type</td>
                  <td data-test="asset-details-type">{props.productType}</td>
                </tr>
              )}

              <tr>
                <td>Product Manufacturer</td>
                <td data-test="asset-details-manufacturer">
                  {props.productManufacturer !== null ? translateManufacturer(props.productManufacturer) : "None"}
                </td>
              </tr>

              <tr>
                <td>Product Part Number</td>
                <td data-test="asset-details-part-number">
                  {props.productModel !== null ? props.productModel : "None"}
                </td>
              </tr>

              <tr>
                <td>Product Serial Number</td>
                <td data-test="asset-details-serial-number">
                  {props.productIdentifier !== null ? props.productIdentifier : "None"}
                </td>
              </tr>

              <tr>
                <td>Controller</td>
                <td data-test="asset-details-controller">
                  {props.controllerModel !== null ? props.controllerModel : "None"}
                </td>
              </tr>

              <tr>
                <td>
                  <span className="d-none d-sm-inline">Monitoring Device</span>
                  <span className="d-inline d-sm-none">Device</span>
                </td>
                <td data-test="asset-details-device">
                  <span className="me-2">
                    {props.deviceTypeName !== null && props.deviceIdentifier !== null ? (
                      <span>
                        {props.deviceTypeName} {props.deviceIdentifier}
                        {deviceCanSample && props.allowDeviceRead && (
                          <SampleDeviceDataButton assetId={props.assetId || 0} className="ms-2 me-2" />
                        )}
                        {deviceCanValidate && props.allowAssetValidate && (
                          <ValidateAssetButton
                            assetId={props.assetId || null}
                            deviceId={props.deviceId || null}
                            deviceType={props.deviceType || null}
                          />
                        )}
                      </span>
                    ) : (
                      <span>None</span>
                    )}
                  </span>
                </td>
              </tr>

              {otherAssetInNetworkHasMonitoringDevice(props.isInNetwork, gatewayAsset) && (
                <tr>
                  <td>
                    <span className="d-none d-sm-inline">Network Monitoring Device</span>
                    <span className="d-inline d-sm-none">Network Device</span>
                    <IconTooltip
                      id="gateway-device-tooltip"
                      icon="info-circle"
                      message="This asset is collecting data from another asset's monitoring device that exists in the same network."
                      color="var(--info-tooltip)"
                    />
                  </td>
                  <td data-test="asset-details-device">
                    <span className="me-2">
                      <span>
                        {(gatewayAsset as NetworkAsset).networkDeviceTypeName}{" "}
                        {(gatewayAsset as NetworkAsset).networkDeviceIdentifier}
                        {deviceCanSample && props.allowDeviceRead && (
                          <SampleDeviceDataButton assetId={props.assetId || 0} className="ms-2 me-2" />
                        )}
                      </span>
                    </span>
                    {deviceCanValidate && props.allowAssetValidate && (
                      <ValidateAssetButton
                        assetId={props.assetId || null}
                        deviceId={props.deviceId || null}
                        deviceType={props.deviceType || null}
                      />
                    )}
                  </td>
                </tr>
              )}

              {props.showAssociatedAssetgroups && assetgroupsLabel.length > 0 && (
                <tr>
                  <td>
                    Asset Groups{" "}
                    <IconTooltip
                      id="asset-group-association-tooltip"
                      icon="info-circle"
                      message={
                        "Lists the asset groups that this asset is directly associated with. " +
                        "This does not include asset groups that are parents of the directly associated asset groups."
                      }
                      color="var(--info-tooltip)"
                    />
                  </td>
                  <td>
                    <span className="me-2">{assetgroupsLabel}</span>
                  </td>
                </tr>
              )}

              {otherAssetsExistInNetwork(props.isInNetwork, props.networkAssets) && (
                <tr>
                  <td>
                    Network Assets{" "}
                    <IconTooltip
                      id="network-assets-tooltip"
                      icon="info-circle"
                      message={
                        "Lists the assets that are in the same network as this asset. " +
                        "A network is used to allow a single monitoring device to divide " +
                        "collected data across multiple assets."
                      }
                      color="var(--info-tooltip)"
                    />
                  </td>
                  <td>
                    <span className="me-2">
                      {(props.networkAssets as NetworkAsset[]).map((networkAsset, i) => (
                        <Fragment key={networkAsset.assetId}>
                          {networkAsset.userHasAccess ? (
                            <Link to={`/asset/${networkAsset.assetId}/data`} className={styles.link}>
                              {networkAsset.assetName}
                            </Link>
                          ) : (
                            <span>{networkAsset.assetName}</span>
                          )}
                          {networkAsset.isGateway && ` (Gateway)`}
                          {props.networkAssets !== null &&
                            props.networkAssets !== undefined &&
                            i < props.networkAssets.length - 1 &&
                            ", "}
                        </Fragment>
                      ))}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface Props {
  name: string;
  nickname: string | null;
  productType?: string;
  productModel: string;
  productIdentifier: string;
  productManufacturer: string;
  deviceType: string | null;
  deviceTypeName: string | null;
  deviceId?: number | null;
  deviceIdentifier: string | null;
  controllerModel: string | null;
  loading: boolean;
  isRented: boolean;
  isInNetwork?: boolean;
  assetId?: number;
  showAssociatedAssetgroups?: boolean;
  allowDeviceRead?: boolean;
  allowAssetValidate?: boolean;
  networkAssets?: NetworkAsset[];
}

interface ResponseBody {
  assetgroups: AssociatedAssetgroup[];
}

interface AssociatedAssetgroup {
  assetgroupId: number;
  name: string;
}

interface NetworkAsset {
  assetId: number;
  assetName: string;
  isGateway: boolean;
  networkDeviceTypeModel: string;
  networkDeviceTypeName: string;
  networkDeviceIdentifier: string;
  userHasAccess: boolean;
}
